"use client";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/shared/lib/utils";
import { ExersiseTypes } from "@l_learn/shared";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Button } from "~/components/ui/button";
import { ExerciseContent } from "~/widgets/DialogWidget/ui/ExerciseContent";

// Context for sharing state between compound components
interface EasyExerciseContextValue {
	exercise: ExersiseTypes.EasyExercise;
	selectedAnswers: string[];
	activeGapIndex: number | null;
	totalGaps: number;
	isSubmitted: boolean;
	handleAnswerChange: (gapIndex: number, answer: string) => void;
	handleVariantClick: (variantText: string) => void;
	handleGapClick: (gapIndex: number) => void;
	setActiveGapIndex: (index: number | null) => void;
}

const EasyExerciseContext = createContext<EasyExerciseContextValue | null>(null);

const useEasyExercise = () => {
	const context = useContext(EasyExerciseContext);
	if (!context) {
		throw new Error("EasyExercise compound components must be used within EasyExerciseUI");
	}
	return context;
};

// Root component
interface EasyExerciseUIProps {
	exercise: ExersiseTypes.EasyExercise;
	selectedAnswers?: string[];
	onAnswerChange?: (gapIndex: number, answer: string) => void;
	isSubmitted?: boolean;
	children: ReactNode;
}

const EasyExerciseRoot = ({
	exercise,
	selectedAnswers: externalSelectedAnswers,
	onAnswerChange,
	isSubmitted = false,
	children
}: EasyExerciseUIProps) => {
	// Count total gaps in the exercise
	const totalGaps = exercise.text.filter(
		(item) => item.type === ExersiseTypes.Content.FillGap
	).length;

	// Internal state for uncontrolled mode
	const [internalSelectedAnswers, setInternalSelectedAnswers] = useState<string[]>(
		Array(totalGaps).fill("")
	);
	const [activeGapIndex, setActiveGapIndex] = useState<number | null>(null);

	// Use external state if provided (controlled), otherwise use internal state (uncontrolled)
	const selectedAnswers = externalSelectedAnswers ?? internalSelectedAnswers;

	// Auto-select first empty gap when component mounts
	useEffect(() => {
		if (!isSubmitted && activeGapIndex === null) {
			const firstEmptyGap = selectedAnswers.findIndex((answer) => !answer);
			if (firstEmptyGap !== -1) {
				setActiveGapIndex(firstEmptyGap);
			}
		}
	}, [selectedAnswers, activeGapIndex, isSubmitted]);

	const handleAnswerChange = (gapIndex: number, answer: string) => {
		if (onAnswerChange) {
			onAnswerChange(gapIndex, answer);
		} else {
			const newAnswers = [...internalSelectedAnswers];
			newAnswers[gapIndex] = answer;
			setInternalSelectedAnswers(newAnswers);
		}
	};

	const handleVariantClick = (variantText: string) => {
		if (isSubmitted || activeGapIndex === null) return;

		handleAnswerChange(activeGapIndex, variantText);

		// Move to next empty gap or deactivate
		const nextEmptyGap = selectedAnswers.findIndex(
			(answer, index) => index > activeGapIndex && !answer
		);

		if (nextEmptyGap !== -1) {
			setActiveGapIndex(nextEmptyGap);
		} else {
			setActiveGapIndex(null);
		}
	};

	const handleGapClick = (gapIndex: number) => {
		if (!isSubmitted) {
			setActiveGapIndex(gapIndex);
		}
	};

	const contextValue: EasyExerciseContextValue = {
		exercise,
		selectedAnswers,
		activeGapIndex,
		totalGaps,
		isSubmitted,
		handleAnswerChange,
		handleVariantClick,
		handleGapClick,
		setActiveGapIndex
	};

	return (
		<EasyExerciseContext.Provider value={contextValue}>
			<div className="space-y-4">{children}</div>
		</EasyExerciseContext.Provider>
	);
};

// Content component (text with gaps)
const Content = () => {
	const { exercise, selectedAnswers, activeGapIndex, isSubmitted, handleGapClick } =
		useEasyExercise();

	const renderGap = (gapIndex: number) => {
		const isActive = activeGapIndex === gapIndex;
		const hasAnswer = selectedAnswers[gapIndex] && selectedAnswers[gapIndex].trim() !== "";

		return (
			<Button
				type="button"
				onClick={() => handleGapClick(gapIndex)}
				disabled={isSubmitted}
				className={cn(
					"inline-block min-w-[120px] mx-2 px-3 py-1 transition-all",
					isActive && !isSubmitted ? "border-accent bg-secondary" : "border-gray-300",
					hasAnswer
						? "bg-green-100 "
						: "bg-secondary text-foreground hover:bg-secondary/90",
					isSubmitted && "cursor-not-allowed opacity-75"
				)}
			>
				{hasAnswer ? selectedAnswers[gapIndex] : "..."}
			</Button>
		);
	};

	return <ExerciseContent text={exercise.text} renderGap={renderGap} />;
};

// Active gap indicator component
const ActiveGapIndicator = () => {
	const { activeGapIndex, isSubmitted } = useEasyExercise();

	if (activeGapIndex === null || isSubmitted) return null;

	return (
		<Alert className="">
			<AlertDescription className="text-foreground">
				Select an answer for gap {activeGapIndex + 1}:
			</AlertDescription>
		</Alert>
	);
};

// Variants component (answer options)
const Variants = () => {
	const { exercise, selectedAnswers, activeGapIndex, isSubmitted, handleVariantClick } =
		useEasyExercise();

	return (
		<div className="flex flex-wrap gap-2">
			{exercise.variants.map((variant) => {
				const isSelected =
					activeGapIndex !== null && selectedAnswers[activeGapIndex] === variant.text;
				const isUsedElsewhere = selectedAnswers.some(
					(answer, index) => index !== activeGapIndex && answer === variant.text
				);

				return (
					<Button
						key={variant.text}
						onClick={() => handleVariantClick(variant.text)}
						disabled={isSubmitted || activeGapIndex === null}
						variant={isSelected ? "default" : "outline"}
						className={`
							${
								isSelected
									? "bg-primary text-primary-foreground"
									: "bg-card text-card-foreground hover:bg-accent"
							}
							${isUsedElsewhere && !isSelected ? "opacity-50" : ""}
							${isSubmitted ? "cursor-not-allowed" : ""}
						`}
					>
						{variant.text}
						{isUsedElsewhere && !isSelected && <span className="ml-1 text-xs">✓</span>}
					</Button>
				);
			})}
		</div>
	);
};

// Progress indicator component
const Progress = () => {
	const { selectedAnswers, activeGapIndex, totalGaps, isSubmitted } = useEasyExercise();

	if (isSubmitted) return null;

	return (
		<div className="flex items-center gap-2 text-sm text-muted-foreground">
			<span>Progress:</span>
			<div className="flex gap-1">
				{Array.from({ length: totalGaps }).map((_, index) => (
					<div
						key={`progress-${index}`}
						className={`
							w-3 h-3 rounded-full
							${
								selectedAnswers[index]
									? "bg-green-500"
									: index === activeGapIndex
										? "bg-blue-500"
										: "bg-gray-300"
							}
						`}
					/>
				))}
			</div>
			<Badge variant={"secondary"}>
				{selectedAnswers.filter(Boolean).length} / {totalGaps}
			</Badge>
		</div>
	);
};

// Clear buttons component
const ClearButtons = () => {
	const { selectedAnswers, activeGapIndex, totalGaps, isSubmitted, handleAnswerChange, setActiveGapIndex } =
		useEasyExercise();

	if (isSubmitted) return null;

	return (
		<div className="flex gap-2">
			{activeGapIndex !== null && selectedAnswers[activeGapIndex] && (
				<Button
					onClick={() => handleAnswerChange(activeGapIndex, "")}
					variant="destructive"
					size="sm"
				>
					Clear Gap {activeGapIndex + 1}
				</Button>
			)}

			{selectedAnswers.some(Boolean) && (
				<Button
					onClick={() => {
						for (let i = 0; i < totalGaps; i++) {
							handleAnswerChange(i, "");
						}
						setActiveGapIndex(0);
					}}
					variant="outline"
					size="sm"
				>
					Clear All
				</Button>
			)}
		</div>
	);
};

// Compose the compound component
export const EasyExerciseUI = Object.assign(EasyExerciseRoot, {
	Content,
	ActiveGapIndicator,
	Variants,
	Progress,
	ClearButtons
});

